import {
  Gavel,
  FileText,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { type SourcingEventType } from '@/features/sourcing/types';

export interface LineItem {
  id: string;
  itemName: string;
  sku: string;
  category: string;
  quantity: string;
  unit: string;
  specifications: string;
}

export interface CreateEventFormData {
  title: string;
  eventType: SourcingEventType;
  deliveryZone: string;
  deliverySiteId: string;
  requiredDeliveryDate: string;
  bidDeadline: string;
  auctionStartTime: string;
  auctionEndTime: string;
  startingPrice: string;
  reservePrice: string;
  estimatedValue: string;
  description: string;
  lines: LineItem[];
}

const DELIVERY_ZONES = [
  {
    value: 'Port of Miami',
    label: 'Port of Miami' 
  },
  {
    value: 'Barcelona Port',
    label: 'Barcelona Port' 
  },
  {
    value: 'Southampton',
    label: 'Southampton' 
  },
  {
    value: 'Singapore',
    label: 'Singapore' 
  },
];

interface EventTypeSectionProps {
  readonly formData: CreateEventFormData;
  readonly setFormData: (data: CreateEventFormData) => void;
}

export function EventTypeSection({
  formData, setFormData 
}: EventTypeSectionProps) {
  const isSealed = formData.eventType === 'sealed_proposal';
  return (
    <Card>
      <CardHeader>
        <CardTitle>Event Type</CardTitle>
        <CardDescription>Choose the type of sourcing event</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setFormData({
              ...formData,
              eventType: 'reverse_auction' 
            })}
            className={`p-4 rounded-lg border-2 text-left transition-colors ${
              formData.eventType === 'reverse_auction'
                ? 'border-orange-500 bg-orange-50'
                : 'border-muted hover:border-muted-foreground/50'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Gavel className="h-5 w-5 text-orange-600" />
              </div>
              <span className="font-semibold">Reverse Auction</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Live bidding where suppliers compete to offer the lowest price in real-time
            </p>
          </button>
          <button
            type="button"
            onClick={() => setFormData({
              ...formData,
              eventType: 'sealed_proposal' 
            })}
            className={`p-4 rounded-lg border-2 text-left transition-colors ${
              isSealed
                ? 'border-indigo-500 bg-indigo-50'
                : 'border-muted hover:border-muted-foreground/50'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <FileText className="h-5 w-5 text-indigo-600" />
              </div>
              <span className="font-semibold">Sealed Proposal</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Suppliers submit hidden bids by a deadline, evaluated after closing
            </p>
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

interface EventDetailsSectionProps {
  readonly formData: CreateEventFormData;
  readonly setFormData: (data: CreateEventFormData) => void;
}

export function EventDetailsSection({
  formData, setFormData 
}: EventDetailsSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Event Details</CardTitle>
        <CardDescription>Basic information about the sourcing event</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Title <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({
                ...formData,
                title: e.target.value 
              })}
              placeholder="e.g., Q1 Seafood Procurement"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Delivery Zone <span className="text-destructive">*</span>
            </label>
            <select
              value={formData.deliveryZone}
              onChange={(e) => setFormData({
                ...formData,
                deliveryZone: e.target.value 
              })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Select zone...</option>
              {DELIVERY_ZONES.map((zone) => (
                <option key={zone.value} value={zone.value}>
                  {zone.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Bid Deadline <span className="text-destructive">*</span>
            </label>
            <input
              type="datetime-local"
              value={formData.bidDeadline}
              onChange={(e) => setFormData({
                ...formData,
                bidDeadline: e.target.value 
              })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Required Delivery Date</label>
            <input
              type="date"
              value={formData.requiredDeliveryDate}
              onChange={(e) => setFormData({
                ...formData,
                requiredDeliveryDate: e.target.value 
              })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium mb-1.5 block">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({
              ...formData,
              description: e.target.value 
            })}
            placeholder="Event description and requirements..."
            rows={3}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      </CardContent>
    </Card>
  );
}

interface AuctionSettingsSectionProps {
  readonly formData: CreateEventFormData;
  readonly setFormData: (data: CreateEventFormData) => void;
}

export function AuctionSettingsSection({
  formData, setFormData 
}: AuctionSettingsSectionProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Auction Settings</CardTitle>
        <CardDescription>Configure the live auction parameters</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Auction Start Time <span className="text-destructive">*</span>
            </label>
            <input
              type="datetime-local"
              value={formData.auctionStartTime}
              onChange={(e) => setFormData({
                ...formData,
                auctionStartTime: e.target.value 
              })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">
              Auction End Time <span className="text-destructive">*</span>
            </label>
            <input
              type="datetime-local"
              value={formData.auctionEndTime}
              onChange={(e) => setFormData({
                ...formData,
                auctionEndTime: e.target.value 
              })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Starting Price ($)</label>
            <input
              type="number"
              min="0"
              value={formData.startingPrice}
              onChange={(e) => setFormData({
                ...formData,
                startingPrice: e.target.value 
              })}
              placeholder="100000"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Reserve Price ($)</label>
            <input
              type="number"
              min="0"
              value={formData.reservePrice}
              onChange={(e) => setFormData({
                ...formData,
                reservePrice: e.target.value 
              })}
              placeholder="80000"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

